import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { markConsultationOpened } from "../lib/conversionState";

const ConsultationContext = createContext({
  open: () => {},
  close: () => {},
  isOpen: false,
  prefill: null,
});

export const ConsultationProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const open = useCallback((data = null) => {
    setPrefill(data);
    setIsOpen(true);
    // Mark that the visitor has crossed the "conversation" threshold,
    // which removes them from the Circular Behavior orbit.
    markConsultationOpened();
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Keep prefill briefly so the closing animation reads the same context;
    // then clear on next tick to avoid stale prefills.
    setTimeout(() => setPrefill(null), 200);
  }, []);

  // Listen for a global `am:open-consultation` event so any leaf component
  // (e.g. the Work With Me ladder buttons on the homepage) can request a
  // consultation modal without prop drilling. The event detail is passed
  // straight through as prefill so the modal can read tier/label.
  useEffect(() => {
    const handler = (e) => open(e?.detail || null);
    window.addEventListener("am:open-consultation", handler);
    return () => window.removeEventListener("am:open-consultation", handler);
  }, [open]);

  const value = useMemo(
    () => ({ open, close, isOpen, prefill }),
    [open, close, isOpen, prefill]
  );

  return (
    <ConsultationContext.Provider value={value}>
      {children}
    </ConsultationContext.Provider>
  );
};

export const useConsultation = () => useContext(ConsultationContext);
